import { useEffect, useRef } from 'react';
import { LocateFixed, Maximize, Minimize } from 'lucide-react';
import { useMap } from 'react-leaflet';

import { useProjectSiteParams } from '@/shared/lib';
import { useTranslation } from 'react-i18next';

import { useFullscreen } from '../../../model/use-fullscreen';

interface Positioned {
  lat: number;
  lng: number;
}

interface MapViewManagerProps {
  points: Positioned[];
}

/**
 * Keeps the map sized to its box, fits it to the data once per project/site, and
 * offers the reset control the user guide describes next to the zoom buttons.
 * Refitting on every data update is deliberately not done — it would yank the map
 * away while the operator is reading it.
 */
export function MapViewManager({ points }: MapViewManagerProps) {
  const map = useMap();
  const { t } = useTranslation();

  // The wrapper, not the Leaflet container: it is what holds the legend, the layer
  // controls and the timeline, and fullscreen should carry them along with the map.
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(() => map.getContainer().parentElement);
  const { projectId, siteId } = useProjectSiteParams();
  const hasFitted = useRef(false);

  // The observer is set up once, so it reaches the current `fit` through a ref
  // instead of being rebuilt whenever the data changes identity.
  const fitRef = useRef(() => {});

  const fit = () => {
    const { x, y } = map.getSize();
    // Fitting a box the browser has not laid out yet picks a nonsense zoom.
    if (points.length === 0 || x === 0 || y === 0) return;

    map.fitBounds(
      points.map((point) => [point.lat, point.lng]),
      { padding: [50, 50] }
    );
    hasFitted.current = true;
  };

  fitRef.current = fit;

  useEffect(() => {
    hasFitted.current = false;
  }, [projectId, siteId]);

  /**
   * Leaflet measures its container once, at init. The map fills the space its flex
   * parents leave it, which is still zero on the first paint — so without this the
   * tiles never appear. The same observer keeps the map honest when the window or
   * the sidebar changes its width.
   */
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      map.invalidateSize();
      if (!hasFitted.current) fitRef.current();
    });

    observer.observe(map.getContainer());

    return () => observer.disconnect();
  }, [map]);

  useEffect(() => {
    if (hasFitted.current) return;

    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  const FullscreenIcon = isFullscreen ? Minimize : Maximize;

  return (
    <div className="leaflet-top leaflet-left" style={{ top: 80 }}>
      <div className="leaflet-control leaflet-bar">
        <button
          type="button"
          className="map-bar-button"
          onClick={toggleFullscreen}
          aria-label={t(isFullscreen ? 'welcome.map.fullscreen.exit.label' : 'welcome.map.fullscreen.label')}
          title={t(isFullscreen ? 'welcome.map.fullscreen.exit.label' : 'welcome.map.fullscreen.label')}>
          <FullscreenIcon className="h-4 w-4" />
        </button>

        <button
          type="button"
          className="map-bar-button"
          onClick={fit}
          aria-label={t('welcome.map.fit.label')}
          title={t('welcome.map.fit.label')}>
          <LocateFixed className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
